import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Close from "@mui/icons-material/Close";
import {
  Alert,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { ReactNode, useEffect } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router";
import { GetPortraitResponse } from "tng-address-collection-shared/types/portraitsApiTypes.ts";
import { usePortraitsTranslator } from "../i18n/translator";
import { PortraitFlow } from "../portrait/PortraitFlow";
import {
  colleaguesPortrait,
  PortraitSubjectContext,
} from "../portrait/portraitSubject";
import { useFlagPortrait, usePortrait } from "../portrait/usePortrait";
import { ColleagueNavigation } from "./galleryFilters";
import { useGalleryEntrySync, useListPortraits } from "./useAdminPortraits";

const PanelButton = ({
  label,
  disabled = false,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) => (
  <Tooltip title={label}>
    {/* A disabled button fires no events for the tooltip to follow */}
    <span>
      <IconButton aria-label={label} disabled={disabled} onClick={onClick}>
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

const FlagSwitch = ({ portrait }: { portrait: GetPortraitResponse }) => {
  const translator = usePortraitsTranslator();
  const flag = useFlagPortrait();

  return (
    <Stack spacing={1}>
      <FormControlLabel
        control={
          <Switch
            color="warning"
            checked={portrait.flagged}
            onChange={(event) => flag.mutate({ flagged: event.target.checked })}
          />
        }
        label={translator("flaggedAsUnsuitable")}
        disabled={flag.isPending || portrait.cutout?.imageUrl === undefined}
      />
      {flag.isError && (
        <Alert severity="error">
          {translator("unknownErrorTryAgainLater")}
        </Alert>
      )}
    </Stack>
  );
};

const ColleaguePortrait = ({ uidNumber }: { uidNumber: number }) => {
  const name = useListPortraits().data?.employees.find(
    (employee) => employee.uidNumber === uidNumber,
  )?.name;
  const { data: portrait } = usePortrait();
  useGalleryEntrySync(uidNumber, portrait);

  return (
    <PortraitFlow
      name={name}
      generateOnArrival={false}
      renderFlag={(portrait) => <FlagSwitch portrait={portrait} />}
    />
  );
};

export const ColleaguePanel = () => {
  const translator = usePortraitsTranslator();
  const navigate = useNavigate();
  const uidNumber = Number(useParams().uidNumber);
  const { previous, next, position, count } = useOutletContext<
    ColleagueNavigation | undefined
  >() ?? { count: 0 };

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      // Popups such as the colleague search close first
      if (event.key === "Escape" && !event.defaultPrevented) {
        void navigate("/admin");
      }
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [navigate]);

  return (
    <Stack spacing={1}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center">
          <PanelButton
            label={translator("previousPortrait")}
            disabled={previous === undefined}
            onClick={() => void navigate(`/admin/${previous}`)}
          >
            <ChevronLeft />
          </PanelButton>
          {position !== undefined && (
            <Typography variant="body2">
              {position} / {count}
            </Typography>
          )}
          <PanelButton
            label={translator("nextPortrait")}
            disabled={next === undefined}
            onClick={() => void navigate(`/admin/${next}`)}
          >
            <ChevronRight />
          </PanelButton>
        </Stack>
        <PanelButton
          label={translator("closePanel")}
          onClick={() => void navigate("/admin")}
        >
          <Close />
        </PanelButton>
      </Stack>
      <PortraitSubjectContext.Provider value={colleaguesPortrait(uidNumber)}>
        <ColleaguePortrait key={uidNumber} uidNumber={uidNumber} />
      </PortraitSubjectContext.Provider>
    </Stack>
  );
};
